import {
  uploadDocument,
  deleteDocument,
  replaceDocument,
  getDocumentsForCollection,
  getDocumentationChunks,
  getCollections,
  createCollection,
  updateCollection,
  deleteCollection,
} from '../api/client.js';
import { isValidFileType, showError } from '../utils/validation.js';

// DOM elements
let collectionSelect, docNameInput, fileInput, uploadZone, browseBtn;
let fileInfo, fileNameEl, changeFileBtn, uploadBtn, uploadStatus;
let listCollectionSelect, refreshBtn, docList;
let listStaleOnlyCheckbox, listStaleDaysInput;
let newKbNameInput, newKbLabelInput, createKbBtn, kbList, kbStatus;
let selectedFile = null;

export function initAdmin() {
  // Knowledge base management elements
  newKbNameInput = document.getElementById('adminNewKbName');
  newKbLabelInput = document.getElementById('adminNewKbLabel');
  createKbBtn = document.getElementById('adminCreateKbBtn');
  kbList = document.getElementById('adminKbList');
  kbStatus = document.getElementById('adminKbStatus');

  // Upload form elements
  collectionSelect = document.getElementById('adminCollectionSelect');
  docNameInput = document.getElementById('adminDocName');
  fileInput = document.getElementById('adminFileInput');
  uploadZone = document.getElementById('adminUploadZone');
  browseBtn = document.getElementById('adminBrowseBtn');
  fileInfo = document.getElementById('adminFileInfo');
  fileNameEl = document.getElementById('adminFileName');
  changeFileBtn = document.getElementById('adminChangeFileBtn');
  uploadBtn = document.getElementById('adminUploadBtn');
  uploadStatus = document.getElementById('adminUploadStatus');

  // Document list elements
  listCollectionSelect = document.getElementById('adminListCollection');
  refreshBtn = document.getElementById('adminRefreshBtn');
  docList = document.getElementById('adminDocList');
  listStaleOnlyCheckbox = document.getElementById('adminListStaleOnly');
  listStaleDaysInput = document.getElementById('adminListStaleDays');
  syncStaleFilterControls();

  if (!uploadZone) return;

  // Knowledge base management. Data is NOT loaded here — initAdmin runs on
  // page load, possibly before login (first visit), where the request would
  // 401. main.js triggers loadKnowledgeBases() after auth and on tab entry.
  if (createKbBtn) {
    createKbBtn.addEventListener('click', handleCreateKb);
  }

  // File selection
  browseBtn.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', handleFileSelect);
  changeFileBtn.addEventListener('click', resetFile);

  // Drag and drop
  uploadZone.addEventListener('dragover', (e) => {
    e.preventDefault();
    uploadZone.classList.add('drag-over');
  });
  uploadZone.addEventListener('dragleave', () =>
    uploadZone.classList.remove('drag-over')
  );
  uploadZone.addEventListener('drop', handleDrop);

  // Upload
  uploadBtn.addEventListener('click', handleUpload);

  // Document list
  listCollectionSelect.addEventListener('change', () => loadDocumentList());
  refreshBtn.addEventListener('click', () => loadDocumentList());
  if (listStaleOnlyCheckbox) {
    listStaleOnlyCheckbox.addEventListener('change', () => {
      syncStaleFilterControls();
      loadDocumentList();
    });
  }
  if (listStaleDaysInput) {
    listStaleDaysInput.addEventListener('change', () => loadDocumentList());
  }
}

function handleFileSelect(e) {
  const file = e.target.files[0];
  if (file && isValidFileType(file)) {
    selectedFile = file;
    showSelectedFile(file);
    autoSuggestName(file);
  } else if (file) {
    showError('Ungültiger Dateityp. Bitte .docx, .pdf, .md oder .txt wählen.');
  }
}

function handleDrop(e) {
  e.preventDefault();
  uploadZone.classList.remove('drag-over');
  const file = e.dataTransfer.files[0];
  if (file && isValidFileType(file)) {
    selectedFile = file;
    showSelectedFile(file);
    autoSuggestName(file);
  } else if (file) {
    showError('Ungültiger Dateityp.');
  }
}

function autoSuggestName(file) {
  if (!docNameInput.value.trim()) {
    docNameInput.value = file.name
      .replace(/\.[^.]+$/, '')
      .replace(/[-_]/g, ' ');
  }
}

function showSelectedFile(file) {
  fileNameEl.textContent = file.name;
  uploadZone.style.display = 'none';
  fileInfo.style.display = 'flex';
  uploadBtn.style.display = 'inline-flex';
}

function resetFile() {
  selectedFile = null;
  fileInput.value = '';
  uploadZone.style.display = 'block';
  fileInfo.style.display = 'none';
  uploadBtn.style.display = 'none';
}

function syncStaleFilterControls() {
  if (!listStaleOnlyCheckbox || !listStaleDaysInput) return;
  const enabled = listStaleOnlyCheckbox.checked;
  listStaleDaysInput.disabled = !enabled;
  listStaleDaysInput.classList.toggle('disabled', !enabled);
}

// --- Knowledge Base Management ---

let kbStatusTimer = null;
function showKbStatus(message, type) {
  if (!kbStatus) return;
  if (kbStatusTimer) clearTimeout(kbStatusTimer);
  kbStatus.textContent = message;
  kbStatus.style.display = 'block';
  kbStatus.className = `admin-upload-status status-${type}`;
  if (type === 'success') {
    kbStatusTimer = setTimeout(() => {
      kbStatus.style.display = 'none';
      kbStatusTimer = null;
    }, 5000);
  }
}

export async function loadKnowledgeBases() {
  try {
    const data = await getCollections();
    renderKbList(data.collections || []);
    populateCollectionSelects(data.collections || []);
  } catch (error) {
    if (kbList) {
      {
      const _e = document.createElement("div");
      _e.className = "error-message";
      _e.textContent = `Fehler: ${error.message}`;
      kbList.replaceChildren(_e);
    }
    }
  }
}

function populateCollectionSelects(collections) {
  [collectionSelect, listCollectionSelect].forEach((select) => {
    if (!select) return;
    const previous = select.value;
    select.innerHTML = '';
    collections.forEach((col) => {
      const option = document.createElement('option');
      option.value = col.name;
      option.textContent = col.label && col.label !== col.name
        ? `${col.label} (${col.name})`
        : col.name;
      select.appendChild(option);
    });
    if (previous && collections.some((col) => col.name === previous)) {
      select.value = previous;
    }
  });
  loadDocumentList();
}

function renderKbList(collections) {
  if (!kbList) return;
  kbList.innerHTML = '';
  if (!collections.length) {
    kbList.innerHTML = '<div class="no-results">Keine Wissensdatenbanken vorhanden.</div>';
    return;
  }

  collections.forEach((col) => {
    const row = document.createElement('div');
    row.className = 'admin-doc-row';

    const infoWrapper = document.createElement('div');
    infoWrapper.className = 'admin-doc-info';

    const nameEl = document.createElement('span');
    nameEl.className = 'admin-doc-name';
    nameEl.textContent = col.label || col.name;
    infoWrapper.appendChild(nameEl);

    const metaEl = document.createElement('span');
    metaEl.className = 'admin-doc-meta';
    metaEl.textContent = `${col.name} · ${col.count} Abschnitte`;
    infoWrapper.appendChild(metaEl);

    const actions = document.createElement('div');
    actions.className = 'admin-doc-actions';

    // Inline rename: swap the label for an input until confirmed/cancelled
    const renameBtn = document.createElement('button');
    renameBtn.className = 'btn-secondary btn-small';
    renameBtn.textContent = 'Umbenennen';
    renameBtn.addEventListener('click', () => {
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'search-input';
      input.maxLength = 100;
      input.value = col.label || col.name;
      input.style.maxWidth = '220px';
      nameEl.replaceWith(input);
      input.focus();
      input.select();

      let finished = false;
      const finish = async (save) => {
        if (finished) return;
        finished = true;
        const newLabel = input.value.trim();
        if (save && newLabel && newLabel !== (col.label || col.name)) {
          try {
            await updateCollection(col.name, newLabel);
            showKbStatus(`Anzeigename von "${col.name}" aktualisiert.`, 'success');
            loadKnowledgeBases();
            return;
          } catch (err) {
            showKbStatus(`Umbenennen fehlgeschlagen: ${err.message}`, 'error');
          }
        }
        input.replaceWith(nameEl);
      };

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') finish(true);
        if (e.key === 'Escape') finish(false);
      });
      input.addEventListener('blur', () => finish(true));
    });
    actions.appendChild(renameBtn);

    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'btn-danger btn-small';
    deleteBtn.textContent = 'Löschen';
    deleteBtn.addEventListener('click', async () => {
      const nonEmpty = col.count > 0;
      const question = nonEmpty
        ? `Wissensdatenbank "${col.name}" enthält ${col.count} Abschnitte. Wirklich unwiderruflich löschen?`
        : `Wissensdatenbank "${col.name}" wirklich löschen?`;
      if (!confirm(question)) return;
      row.classList.add('loading');
      try {
        await deleteCollection(col.name, nonEmpty);
        showKbStatus(`Wissensdatenbank "${col.name}" gelöscht.`, 'success');
        loadKnowledgeBases();
      } catch (err) {
        showKbStatus(`Löschen fehlgeschlagen: ${err.message}`, 'error');
        row.classList.remove('loading');
      }
    });

    actions.appendChild(deleteBtn);
    row.appendChild(infoWrapper);
    row.appendChild(actions);
    kbList.appendChild(row);
  });
}

async function handleCreateKb() {
  const name = newKbNameInput.value.trim().toLowerCase();
  if (!name) {
    showError('Bitte einen Namen für die Wissensdatenbank eingeben.');
    return;
  }

  createKbBtn.disabled = true;
  try {
    const displayName = newKbLabelInput ? newKbLabelInput.value.trim() : '';
    const result = await createCollection(name, displayName);
    showKbStatus(`Wissensdatenbank "${result.display_name || result.name}" angelegt.`, 'success');
    newKbNameInput.value = '';
    if (newKbLabelInput) newKbLabelInput.value = '';
    loadKnowledgeBases();
  } catch (error) {
    showKbStatus(`Fehler: ${error.message}`, 'error');
  } finally {
    createKbBtn.disabled = false;
  }
}

let statusTimer = null;
function showStatus(message, type) {
  if (statusTimer) clearTimeout(statusTimer);
  uploadStatus.textContent = message;
  uploadStatus.style.display = 'block';
  uploadStatus.className = `admin-upload-status status-${type}`;
  if (type === 'success') {
    statusTimer = setTimeout(() => {
      uploadStatus.style.display = 'none';
      statusTimer = null;
    }, 5000);
  }
}

async function handleUpload() {
  if (!selectedFile) {
    showError('Bitte zuerst eine Datei auswählen.');
    return;
  }
  const docName = docNameInput.value.trim();
  if (!docName) {
    showError('Bitte einen Dokumentnamen eingeben.');
    return;
  }
  const collection = collectionSelect.value;
  if (!collection) {
    showError('Bitte eine Wissensdatenbank auswählen.');
    return;
  }

  uploadBtn.disabled = true;
  uploadBtn.textContent = 'Wird hochgeladen...';
  showStatus('Dokument wird verarbeitet und eingebettet...', 'info');

  try {
    const result = await uploadDocument(selectedFile, docName, collection);
    showStatus(
      `Erfolgreich: "${result.document_name}" mit ${result.chunks_created} Abschnitten hochgeladen.`,
      'success'
    );
    resetFile();
    docNameInput.value = '';
    loadKnowledgeBases();
    if (listCollectionSelect.value === collection) {
      loadDocumentList();
    }
  } catch (error) {
    showStatus(`Fehler: ${error.message}`, 'error');
  } finally {
    uploadBtn.disabled = false;
    uploadBtn.textContent = 'Hochladen';
  }
}

// --- Document List ---

export async function loadDocumentList() {
  if (!docList) return;
  const collection = listCollectionSelect.value;
  if (!collection) {
    docList.innerHTML = '<div class="no-results">Keine Wissensdatenbank ausgewählt.</div>';
    return;
  }
  const staleOnly = listStaleOnlyCheckbox ? listStaleOnlyCheckbox.checked : false;
  const staleAfterDaysRaw = listStaleDaysInput
    ? Number.parseInt(listStaleDaysInput.value || '180', 10)
    : 180;
  const staleAfterDays = Number.isFinite(staleAfterDaysRaw) && staleAfterDaysRaw > 0
    ? staleAfterDaysRaw
    : 180;

  docList.innerHTML =
    '<div class="loading-spinner"><div class="spinner"></div></div>';

  try {
    const data = await getDocumentsForCollection(collection, true, {
      staleOnly,
      staleAfterDays,
      includeUnknownStale: true,
    });
    renderDocumentList(
      data.documents,
      collection,
      {
        staleOnly,
        staleAfterDays: staleOnly ? staleAfterDays : 180,
      }
    );
  } catch (error) {
    {
      const _e = document.createElement("div");
      _e.className = "error-message";
      _e.textContent = `Fehler: ${error.message}`;
      docList.replaceChildren(_e);
    }
  }
}

function daysSince(isoDate) {
  const ts = new Date(isoDate).getTime();
  if (Number.isNaN(ts)) return null;
  const diff = Date.now() - ts;
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

function formatDate(isoDate) {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function renderDocumentList(documents, collection, { staleOnly = false, staleAfterDays = 180 } = {}) {
  docList.innerHTML = '';
  if (!documents || documents.length === 0) {
    docList.innerHTML = staleOnly
      ? `<div class="no-results">Keine veralteten Dokumente gefunden (ab ${staleAfterDays} Tagen). Filter anpassen oder deaktivieren.</div>`
      : '<div class="no-results">Keine Dokumente gefunden.</div>';
    return;
  }

  documents.forEach((doc) => {
    // doc is {name, chunk_count, created_at} or a plain string (backward compat)
    const docName = typeof doc === 'string' ? doc : doc.name;

    const row = document.createElement('div');
    row.className = 'admin-doc-row';

    const infoWrapper = document.createElement('div');
    infoWrapper.className = 'admin-doc-info';

    const nameEl = document.createElement('button');
    nameEl.className = 'admin-doc-name admin-doc-name--clickable';
    nameEl.type = 'button';
    nameEl.textContent = docName;
    nameEl.title = 'Abschnitte anzeigen / ausblenden';
    infoWrapper.appendChild(nameEl);

    // Chevron indicator
    const chevron = document.createElement('span');
    chevron.className = 'admin-doc-chevron';
    chevron.textContent = '\u25B8';
    infoWrapper.appendChild(chevron);

    if (typeof doc === 'object' && doc.name) {
      const metaEl = document.createElement('span');
      metaEl.className = 'admin-doc-meta';
      const chunks = doc.chunk_count || '?';
      const age = Number.isFinite(doc.age_days)
        ? doc.age_days
        : (doc.created_at ? daysSince(doc.created_at) : null);
      const dateStr = doc.created_at ? formatDate(doc.created_at) : null;
      const ageText = age !== null
        ? (dateStr ? `${dateStr} (${age} Tage)` : `${age} Tage`)
        : 'Unbekannt';
      metaEl.textContent = `${chunks} Abschnitte · ${ageText}`;
      if (age !== null && age >= staleAfterDays) {
        metaEl.classList.add('stale');
        const badge = document.createElement('span');
        badge.className = 'admin-stale-badge';
        badge.textContent = 'Veraltet';
        metaEl.appendChild(badge);
      }
      infoWrapper.appendChild(metaEl);
    }

    const actions = document.createElement('div');
    actions.className = 'admin-doc-actions';

    // Replace button with hidden file input
    const replaceInput = document.createElement('input');
    replaceInput.type = 'file';
    replaceInput.accept = '.docx,.pdf,.md,.txt';
    replaceInput.hidden = true;
    replaceInput.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (!isValidFileType(file)) {
        showError('Ungültiger Dateityp.');
        return;
      }
      if (!confirm(`Dokument "${docName}" wirklich ersetzen?`)) return;
      row.classList.add('loading');
      try {
        await replaceDocument(docName, file, collection);
        loadDocumentList();
      } catch (err) {
        showError(`Ersetzen fehlgeschlagen: ${err.message}`);
        row.classList.remove('loading');
      }
    });

    const replaceBtn = document.createElement('button');
    replaceBtn.className = 'btn-secondary btn-small';
    replaceBtn.textContent = 'Ersetzen';
    replaceBtn.addEventListener('click', () => replaceInput.click());

    // Delete button
    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'btn-danger btn-small';
    deleteBtn.textContent = 'Löschen';
    deleteBtn.addEventListener('click', async () => {
      if (!confirm(`Dokument "${docName}" wirklich löschen?`)) return;
      row.classList.add('loading');
      try {
        await deleteDocument(docName, collection);
        loadDocumentList();
      } catch (err) {
        showError(`Löschen fehlgeschlagen: ${err.message}`);
        row.classList.remove('loading');
      }
    });

    actions.appendChild(replaceInput);
    actions.appendChild(replaceBtn);
    actions.appendChild(deleteBtn);

    // Chunk preview panel (hidden by default)
    const chunkPanel = document.createElement('div');
    chunkPanel.className = 'admin-chunk-panel';
    chunkPanel.style.display = 'none';

    let chunksLoaded = false;

    nameEl.addEventListener('click', async () => {
      const isOpen = chunkPanel.style.display !== 'none';
      if (isOpen) {
        chunkPanel.style.display = 'none';
        chevron.classList.remove('open');
        chevron.textContent = '▸';
        return;
      }

      chevron.classList.add('open');
      chevron.textContent = '▾';
      chunkPanel.style.display = 'block';

      if (chunksLoaded) return;

      chunkPanel.innerHTML = '<div class="admin-chunk-loading"><div class="spinner"></div> Abschnitte werden geladen...</div>';

      try {
        const data = await getDocumentationChunks(collection, [docName], 50);
        chunksLoaded = true;

        if (!data.chunks || data.chunks.length === 0) {
          chunkPanel.innerHTML = '<div class="admin-chunk-empty">Keine Abschnitte gefunden.</div>';
          return;
        }

        chunkPanel.innerHTML = '';
        data.chunks.forEach((chunk, idx) => {
          const chunkEl = document.createElement('div');
          chunkEl.className = 'admin-chunk-item';

          const headerEl = document.createElement('div');
          headerEl.className = 'admin-chunk-header';
          const sectionLabel = chunk.section || String(idx + 1);
          headerEl.textContent = `Abschnitt ${sectionLabel}`;

          const bodyEl = document.createElement('div');
          bodyEl.className = 'admin-chunk-body';
          bodyEl.textContent = chunk.content || chunk.text || '';

          chunkEl.appendChild(headerEl);
          chunkEl.appendChild(bodyEl);
          chunkPanel.appendChild(chunkEl);
        });

        if (data.has_more) {
          const moreEl = document.createElement('div');
          moreEl.className = 'admin-chunk-more';
          moreEl.textContent = `${data.chunks.length} von ${data.total_chunks} Abschnitten angezeigt. Alle Abschnitte in der Wissensdatenbank einsehbar.`;
          chunkPanel.appendChild(moreEl);
        }
      } catch (err) {
        {
      const _e = document.createElement("div");
      _e.className = "admin-chunk-empty";
      _e.textContent = `Fehler: ${err.message}`;
      chunkPanel.replaceChildren(_e);
    }
      }
    });

    row.appendChild(infoWrapper);
    row.appendChild(actions);
    docList.appendChild(row);
    docList.appendChild(chunkPanel);
  });
}
